import React from 'react';
import {
  Box, Typography, TextField, MenuItem, Checkbox, FormControlLabel,
  Divider, IconButton, Button, Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  PushPin as PinIcon,
  Functions as FunctionsIcon,
  FormatPaint as PaintIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { FORMAT_OPTIONS, ALIGNMENT_OPTIONS } from './FormatPresets';

export default function ColumnConfigPanel({
  selectedColumn,
  columnSettings,
  onUpdateColumnSetting,
  availableColumns,
  selectedColumns,
  onToggleColumn,
  onClose,
  onOpenCalculatedDialog,
  onOpenConditionalDialog,
  calculatedColumns,
  onDeleteCalculatedColumn,
  groupConfig,
  onUpdateGroupConfig,
  showTotalsRow,
  onToggleTotalsRow,
}) {
  const settings = selectedColumn ? (columnSettings[selectedColumn] || {}) : {};

  const handleSettingChange = (field, value) => {
    if (!selectedColumn) return;
    onUpdateColumnSetting(selectedColumn, { ...settings, [field]: value });
  };

  const handleFormatChange = (formatKey) => {
    if (!selectedColumn) return;
    const FORMAT_MAP = {
      text: { type: 'text' },
      currency: { type: 'currency', symbol: '$', decimals: 2 },
      number: { type: 'number', decimals: 2 },
      integer: { type: 'number', decimals: 0 },
      percentage: { type: 'percentage', decimals: 2 },
      date: { type: 'date', pattern: 'MM/DD/YYYY' },
      dateISO: { type: 'date', pattern: 'YYYY-MM-DD' },
    };
    onUpdateColumnSetting(selectedColumn, { ...settings, format: FORMAT_MAP[formatKey] || { type: 'text' } });
  };

  // Find current format key from settings
  const currentFormatKey = (() => {
    const fmt = settings.format;
    if (!fmt || fmt.type === 'text') return 'text';
    if (fmt.type === 'currency') return 'currency';
    if (fmt.type === 'number' && fmt.decimals === 0) return 'integer';
    if (fmt.type === 'number') return 'number';
    if (fmt.type === 'percentage') return 'percentage';
    if (fmt.type === 'date' && fmt.pattern === 'YYYY-MM-DD') return 'dateISO';
    if (fmt.type === 'date') return 'date';
    return 'text';
  })();

  // Groupable columns (non-numeric, non-date for simplicity)
  const numericKeys = ['hours_worked', 'total_pay', 'hourly_rate', 'paid_amount', 'break_minutes', 'subtotal', 'tax_amount', 'total_amount'];
  const groupableColumns = availableColumns.filter(c => !numericKeys.includes(c.key || c.value));

  return (
    <Paper sx={{ p: 2, height: '100%', overflowY: 'auto' }} elevation={0} variant="outlined">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold">Configuration</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Column Selection */}
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>Columns</Typography>
      <Box sx={{ minHeight: 100, maxHeight: 400, overflowY: 'auto', mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1, resize: 'vertical' }}>
        {availableColumns.map(col => {
          const key = col.key || col.value;
          return (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  size="small"
                  checked={selectedColumns.includes(key)}
                  onChange={() => onToggleColumn(key)}
                  sx={{ py: 0.25 }}
                />
              }
              label={<Typography variant="body2" noWrap>{col.label}</Typography>}
              sx={{ display: 'flex', m: 0, mr: 0, width: '100%' }}
            />
          );
        })}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Selected Column Settings */}
      {selectedColumn ? (
        <>
          <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
            Column: {selectedColumn}
          </Typography>

          <TextField
            fullWidth size="small" label="Header Name"
            value={settings.headerName || ''}
            onChange={(e) => handleSettingChange('headerName', e.target.value)}
            sx={{ mb: 1.5 }}
          />

          <TextField
            select fullWidth size="small" label="Format"
            value={currentFormatKey}
            onChange={(e) => handleFormatChange(e.target.value)}
            sx={{ mb: 1.5 }}
          >
            {FORMAT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Alignment"
            value={settings.alignment || 'left'}
            onChange={(e) => handleSettingChange('alignment', e.target.value)}
            sx={{ mb: 1.5 }}
          >
            {ALIGNMENT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth size="small" label="Width (px)" type="number"
            value={settings.width || ''}
            onChange={(e) => handleSettingChange('width', parseInt(e.target.value) || null)}
            sx={{ mb: 1.5 }}
            inputProps={{ min: 50, max: 500 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!settings.wrapText}
                onChange={(e) => handleSettingChange('wrapText', e.target.checked)}
              />
            }
            label={<Typography variant="body2">Word Wrap</Typography>}
            sx={{ mb: 1 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button
              size="small"
              variant={settings.pinned === 'left' ? 'contained' : 'outlined'}
              startIcon={<PinIcon />}
              onClick={() => handleSettingChange('pinned', settings.pinned === 'left' ? null : 'left')}
            >
              {settings.pinned === 'left' ? 'Unfreeze' : 'Freeze'}
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click a column header in the grid to configure it
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Grouping */}
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>Grouping</Typography>
      {(() => {
        // Normalize groupBy to array
        const groupByArr = Array.isArray(groupConfig?.groupBy)
          ? groupConfig.groupBy
          : groupConfig?.groupBy ? [groupConfig.groupBy] : [];
        const level1 = groupByArr[0] || '';
        const level2 = groupByArr[1] || '';

        const handleLevel1Change = (val) => {
          if (!val) {
            onUpdateGroupConfig({ ...groupConfig, groupBy: null });
          } else {
            const newArr = [val];
            if (level2 && level2 !== val) newArr.push(level2);
            onUpdateGroupConfig({ ...groupConfig, groupBy: newArr.length === 1 ? newArr[0] : newArr });
          }
        };

        const handleLevel2Change = (val) => {
          if (!val) {
            onUpdateGroupConfig({ ...groupConfig, groupBy: level1 || null });
          } else {
            onUpdateGroupConfig({ ...groupConfig, groupBy: [level1, val] });
          }
        };

        return (
          <>
            <TextField
              select fullWidth size="small" label="Group By (Level 1)"
              value={level1}
              onChange={(e) => handleLevel1Change(e.target.value)}
              sx={{ mb: 1 }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">None</MenuItem>
              {groupableColumns.map(col => (
                <MenuItem key={col.key || col.value} value={col.key || col.value}>{col.label}</MenuItem>
              ))}
            </TextField>

            {level1 && (
              <TextField
                select fullWidth size="small" label="Group By (Level 2)"
                value={level2}
                onChange={(e) => handleLevel2Change(e.target.value)}
                sx={{ mb: 1 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">None</MenuItem>
                {groupableColumns
                  .filter(col => (col.key || col.value) !== level1)
                  .map(col => (
                    <MenuItem key={col.key || col.value} value={col.key || col.value}>{col.label}</MenuItem>
                  ))}
              </TextField>
            )}
          </>
        );
      })()}

      {(Array.isArray(groupConfig?.groupBy) ? groupConfig.groupBy[0] : groupConfig?.groupBy) && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={groupConfig?.showSubtotals !== false}
                onChange={(e) => onUpdateGroupConfig({ ...groupConfig, showSubtotals: e.target.checked })}
              />
            }
            label={<Typography variant="body2">Show Subtotals</Typography>}
          />
          {groupConfig?.showSubtotals !== false && (
            <Box sx={{ ml: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Subtotal columns:</Typography>
              {selectedColumns.filter(c => numericKeys.includes(c)).map(col => (
                <FormControlLabel
                  key={col}
                  control={
                    <Checkbox
                      size="small"
                      checked={(groupConfig?.subtotalColumns || []).includes(col)}
                      onChange={(e) => {
                        const current = groupConfig?.subtotalColumns || [];
                        const updated = e.target.checked ? [...current, col] : current.filter(c => c !== col);
                        onUpdateGroupConfig({ ...groupConfig, subtotalColumns: updated });
                      }}
                    />
                  }
                  label={<Typography variant="caption">{col}</Typography>}
                  sx={{ display: 'block', m: 0 }}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox size="small" checked={!!showTotalsRow} onChange={(e) => onToggleTotalsRow(e.target.checked)} />
        }
        label={<Typography variant="body2">Show Totals Row</Typography>}
      />

      <Divider sx={{ my: 1.5 }} />

      {/* Calculated Columns */}
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>Calculated Columns</Typography>
      {Object.entries(calculatedColumns || {}).map(([key, calc]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box>
            <Typography variant="body2">{calc.headerName}</Typography>
            <Typography variant="caption" color="text.secondary">{calc.formula}</Typography>
          </Box>
          <IconButton size="small" color="error" onClick={() => onDeleteCalculatedColumn(key)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<FunctionsIcon />} onClick={onOpenCalculatedDialog} sx={{ mt: 0.5 }}>
        Add Calculated Column
      </Button>

      <Divider sx={{ my: 1.5 }} />

      {/* Conditional Formatting */}
      <Button size="small" startIcon={<PaintIcon />} onClick={onOpenConditionalDialog}>
        Conditional Formatting
      </Button>
    </Paper>
  );
}
